import { JobStatuses, RegressionTest } from "../../api/developer";
import { JobStatusCategoricalDistribution } from "../JobStatusCategoricalDistribution";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { CircularProgress, Grid } from "@mui/material";
import { useQuery } from "react-query";
import { gqlQuery } from "../../api/base";
import { gql } from "graphql-request";

type Props = {
  regressionTest: RegressionTest;
};

export const RegressionTestJobStatusCategoricalDistribution = ({
  regressionTest,
}: Props) => {
  // Use the job statuses from the prop as the default/initial value
  const [statuses, setStatuses] = useState(regressionTest.jobStatuses);

  // Update statuses when the prop changes (e.g., from batch query updates)
  useEffect(() => {
    setStatuses(regressionTest.jobStatuses);
  }, [regressionTest.jobStatuses]);

  // We can stop polling when all the jobs are either completed or failed
  const isDone = useMemo(
    () =>
      statuses.sent === 0 && statuses.received === 0 && statuses.running === 0,
    [statuses]
  );

  // Only poll if the test was created within the last 5 minutes
  const shouldPoll = useMemo(() => {
    const created = moment(regressionTest.createdDate);
    const fiveMinutesAgo = moment().subtract(5, "minutes");
    return created.isAfter(fiveMinutesAgo) && !isDone;
  }, [regressionTest.createdDate, isDone]);

  // Lightweight query that only fetches job statuses for polling
  const { data: polledData } = useQuery<{ jobStatuses: JobStatuses }>(
    ["regressionTest", regressionTest.id, "jobStatuses", "poll"],
    () =>
      gqlQuery<{ regressionTest: { jobStatuses: JobStatuses } }>(
        gql`
          query getJobStatuses($regressionTestKey: ObjectId!) {
            regressionTest(regressionTestKey: $regressionTestKey) {
              jobStatuses {
                sent
                received
                running
                failed
                completed
              }
            }
          }
        `,
        { regressionTestKey: regressionTest.id }
      ).then((res) => res.regressionTest),
    {
      enabled: shouldPoll,
      refetchInterval: shouldPoll ? 2000 : false,
    }
  );

  // Update statuses when polling returns new data
  useEffect(() => {
    if (polledData?.jobStatuses) {
      setStatuses(polledData.jobStatuses);
    }
  }, [polledData]);

  return (
    <Grid container>
      {shouldPoll && (
        <Grid item xs="auto" pr={2}>
          <CircularProgress thickness={8} size={10} variant="indeterminate" />
        </Grid>
      )}

      <Grid item xs pt={shouldPoll ? 0.6 : 0}>
        <JobStatusCategoricalDistribution {...statuses} />
      </Grid>
    </Grid>
  );
};
